import React, { useState } from 'react';
import { useSnippet } from '../../contexts/SnippetContext';
import VerificationNotice from '../../components/VerificationNotice';

const Dashboard: React.FC = () => {
  const { hasVerifiedSnippet } = useSnippet();
  const [selectedDate, setSelectedDate] = useState('Jul 27, 2025');
  const [selectedChanges, setSelectedChanges] = useState('Jul 22, 2025');
  const [selectedPlatform, setSelectedPlatform] = useState('All Platforms');
  const [activeTab, setActiveTab] = useState('general');

  // Mock data - in real app this would come from API
  const metrics = {
    brandPresence: {
      visibility: { value: 75, change: 5, trend: 'up' },
      responsesMentioned: { value: 227, change: 45, trend: 'up' }
    },
    citations: {
      totalPagesCited: { value: 1912, change: 592, trend: 'up' },
      myPagesCited: { value: 37, change: 2, trend: 'down' },
      sitesMentioningMe: { value: 778, change: 217, trend: 'up' }
    },
    competitorAnalysis: {
      marketShare: { value: 75, change: 0, trend: 'neutral' },
      marketPosition: { value: 1, change: 0, trend: 'neutral' }
    }
  };

  const competitors = [
    { name: 'Dream Digi (You)', visibility: 75, change: 5, trend: 'up' },
    { name: 'Adobe Firefly', visibility: 57, change: 5, trend: 'up' },
    { name: 'Figma', visibility: 25, change: 2, trend: 'down' },
    { name: 'VistaCreate', visibility: 14, change: 3, trend: 'up' },
    { name: 'Piktochart', visibility: 6, change: 1, trend: 'down' }
  ];

  const visibilityData = [
    { date: 'Jul 22', dreamDigi: 70, adobeFirefly: 52, figma: 27, vistaCreate: 11, piktochart: 7 },
    { date: 'Jul 23', dreamDigi: 72, adobeFirefly: 54, figma: 26, vistaCreate: 12, piktochart: 6 },
    { date: 'Jul 24', dreamDigi: 71, adobeFirefly: 55, figma: 25, vistaCreate: 13, piktochart: 6 },
    { date: 'Jul 25', dreamDigi: 73, adobeFirefly: 56, figma: 24, vistaCreate: 13, piktochart: 6 },
    { date: 'Jul 26', dreamDigi: 74, adobeFirefly: 56, figma: 25, vistaCreate: 14, piktochart: 6 },
    { date: 'Jul 27', dreamDigi: 75, adobeFirefly: 57, figma: 25, vistaCreate: 14, piktochart: 6 }
  ];

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return '↗';
    if (trend === 'down') return '↘';
    return '→';
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'up') return 'text-success';
    if (trend === 'down') return 'text-danger';
    return 'text-muted';
  };

  return (
    <div className="mb-4">
      <VerificationNotice hasVerifiedSnippet={hasVerifiedSnippet} />
      
      {/* Dashboard Header */}
        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form-select form-select-sm"
              style={{width: 'auto'}}
            >
              <option>Jul 27, 2025</option>
              <option>Jul 26, 2025</option>
              <option>Jul 25, 2025</option>
            </select>
            <span className="text-muted small">Changes: {selectedChanges}</span>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="form-select form-select-sm"
              style={{width: 'auto'}}
            >
              <option>All Platforms</option>
              <option>Google</option>
              <option>Bing</option>
              <option>Yahoo</option>
            </select>
            <span className="text-muted small">301 answers</span>
          </div>
        </div>

        {/* Main Tabs */}
        <ul className="nav nav-tabs mb-4" id="dashboardTabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button 
              className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
              type="button"
              role="tab"
              aria-controls="general-tab-pane"
              aria-selected={activeTab === 'general'}
            >
              General
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button 
              className={`nav-link ${activeTab === 'platforms' ? 'active' : ''}`}
              onClick={() => setActiveTab('platforms')}
              type="button"
              role="tab"
              aria-controls="platforms-tab-pane"
              aria-selected={activeTab === 'platforms'}
            >
              Platforms
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button 
              className={`nav-link ${activeTab === 'competitors' ? 'active' : ''}`}
              onClick={() => setActiveTab('competitors')}
              type="button"
              role="tab"
              aria-controls="competitors-tab-pane"
              aria-selected={activeTab === 'competitors'}
            >
              Competitors
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="tab-content" id="dashboardTabContent">
          {/* General Tab */}
          <div 
            className={`tab-pane fade ${activeTab === 'general' ? 'show active' : ''}`}
            id="general-tab-pane"
            role="tabpanel"
            aria-labelledby="general-tab"
            tabIndex={0}
          >
            {/* Key Metrics */}
            <div className="row g-4 mb-4">
          {/* Brand Presence */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Brand Presence</h5>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small mb-1">Visibility</p>
                      <div className="d-flex align-items-center gap-2">
                        <span className="h3 fw-bold">{metrics.brandPresence.visibility.value}%</span>
                        <span className={`small ${getTrendColor(metrics.brandPresence.visibility.trend)}`}>
                          {getTrendIcon(metrics.brandPresence.visibility.trend)} {metrics.brandPresence.visibility.change}%
                        </span>
                      </div>
                    </div>
                    <div className="position-relative" style={{width: '64px', height: '64px'}}>
                      <svg className="w-100 h-100" style={{transform: 'rotate(-90deg)'}} viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#0d6efd"
                          strokeWidth="3"
                          strokeDasharray={`${metrics.brandPresence.visibility.value}, 100`}
                        />
                      </svg>
                      <div className="position-absolute top-50 start-50 translate-middle">
                        <span className="small fw-medium">{metrics.brandPresence.visibility.value}%</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-muted small mb-1">Responses Mentioned</p>
                  <div className="d-flex align-items-center gap-2">
                    <span className="h3 fw-bold">{metrics.brandPresence.responsesMentioned.value}</span>
                    <span className={`small ${getTrendColor(metrics.brandPresence.responsesMentioned.trend)}`}>
                      {getTrendIcon(metrics.brandPresence.responsesMentioned.trend)} {metrics.brandPresence.responsesMentioned.change}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Citations */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Citations</h5>
                <div className="mb-3">
                  <p className="text-muted small mb-1">Total pages cited</p>
                  <div className="d-flex align-items-center gap-2">
                    <span className="h3 fw-bold">{metrics.citations.totalPagesCited.value}</span>
                    <span className={`small ${getTrendColor(metrics.citations.totalPagesCited.trend)}`}>
                      {getTrendIcon(metrics.citations.totalPagesCited.trend)} {metrics.citations.totalPagesCited.change}
                    </span>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-muted small mb-1">My pages cited</p>
                  <div className="d-flex align-items-center gap-2">
                    <span className="h3 fw-bold">{metrics.citations.myPagesCited.value}</span>
                    <span className={`small ${getTrendColor(metrics.citations.myPagesCited.trend)}`}>
                      {getTrendIcon(metrics.citations.myPagesCited.trend)} {metrics.citations.myPagesCited.change}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-muted small mb-1">Sites mentioning me</p>
                  <div className="d-flex align-items-center gap-2">
                    <span className="h3 fw-bold">{metrics.citations.sitesMentioningMe.value}</span>
                    <span className={`small ${getTrendColor(metrics.citations.sitesMentioningMe.trend)}`}>
                      {getTrendIcon(metrics.citations.sitesMentioningMe.trend)} {metrics.citations.sitesMentioningMe.change}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Competitor Analysis */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Competitor Analysis</h5>
                <div className="mb-3">
                  <p className="text-muted small mb-1">Market Share</p>
                  <div className="d-flex align-items-center gap-2">
                    <span className="h3 fw-bold">{metrics.competitorAnalysis.marketShare.value}%</span>
                    <span className={`small ${getTrendColor(metrics.competitorAnalysis.marketShare.trend)}`}>
                      {getTrendIcon(metrics.competitorAnalysis.marketShare.trend)} {metrics.competitorAnalysis.marketShare.change}%
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-muted small mb-1">Market Position</p>
                  <div className="d-flex align-items-center gap-2">
                    <span className="h3 fw-bold">#{metrics.competitorAnalysis.marketPosition.value}</span>
                    <span className={`small ${getTrendColor(metrics.competitorAnalysis.marketPosition.trend)}`}>
                      {getTrendIcon(metrics.competitorAnalysis.marketPosition.trend)} {metrics.competitorAnalysis.marketPosition.change}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Recommendations */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <h5 className="card-title mb-0 text-primary">AI recommendations</h5>
              <i className="fas fa-info-circle text-muted"></i>
            </div>
            <button className="btn btn-link text-muted p-0">
              <i className="fas fa-chevron-down"></i>
            </button>
          </div>
          <div className="card-body">
            <p className="text-muted">AI recommendations will appear here based on your performance data.</p>
          </div>
        </div>

        {/* Visibility Trends Chart */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Visibility trends over time</h5>
            <button className="btn btn-link text-muted p-0">
              <i className="fas fa-download"></i>
            </button>
          </div>
          <div className="card-body">
            <div className="d-flex align-items-end justify-content-between gap-2" style={{height: '256px'}}>
              {visibilityData.map((data, index) => (
                <div key={data.date} className="flex-fill d-flex flex-column align-items-center gap-2">
                  <div className="w-100 d-flex flex-column gap-1">
                    <div 
                      className="bg-primary rounded-top"
                      style={{ height: `${data.dreamDigi * 0.8}%` }}
                    ></div>
                    <div 
                      className="bg-warning rounded-top"
                      style={{ height: `${data.adobeFirefly * 0.8}%` }}
                    ></div>
                    <div 
                      className="bg-success rounded-top"
                      style={{ height: `${data.figma * 0.8}%` }}
                    ></div>
                    <div 
                      className="bg-info rounded-top"
                      style={{ height: `${data.vistaCreate * 0.8}%` }}
                    ></div>
                    <div 
                      className="bg-secondary rounded-top"
                      style={{ height: `${data.piktochart * 0.8}%` }}
                    ></div>
                  </div>
                  <span className="small text-muted">{data.date}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 d-flex justify-content-center gap-4">
              <div className="d-flex align-items-center gap-2">
                <div className="bg-primary rounded" style={{width: '12px', height: '12px'}}></div>
                <span className="small text-muted">Dream Digi (You)</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="bg-warning rounded" style={{width: '12px', height: '12px'}}></div>
                <span className="small text-muted">Adobe Firefly</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="bg-success rounded" style={{width: '12px', height: '12px'}}></div>
                <span className="small text-muted">Figma</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="bg-info rounded" style={{width: '12px', height: '12px'}}></div>
                <span className="small text-muted">VistaCreate</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="bg-secondary rounded" style={{width: '12px', height: '12px'}}></div>
                <span className="small text-muted">Piktochart</span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Visibility Leaderboard */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">AI visibility leaderboard</h5>
            <button className="btn btn-link text-muted p-0">
              <i className="fas fa-download"></i>
            </button>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column gap-3">
              {competitors.map((competitor, index) => (
                <div key={competitor.name} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                  <div className="d-flex align-items-center gap-3">
                    <span className="small text-muted fw-medium">#{index + 1}</span>
                    <span className="fw-medium">{competitor.name}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`small ${getTrendColor(competitor.trend)}`}>
                      {getTrendIcon(competitor.trend)} {competitor.change}%
                    </span>
                    <span className="fw-semibold">{competitor.visibility}%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 d-flex justify-content-between align-items-center">
              <button className="btn btn-link text-primary p-0 d-flex align-items-center gap-2">
                <i className="fas fa-plus"></i>
                <span className="small fw-medium">Track more competitors</span>
              </button>
              <button className="btn btn-link text-primary p-0 small fw-medium">
                View full report →
              </button>
            </div>
          </div>
        </div>

        {/* Platform wise brand visibility */}
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Platform wise brand visibility</h5>
          </div>
          <div className="card-body">
            <p className="text-muted">Platform-specific visibility data will be displayed here.</p>
          </div>
        </div>
          </div>

          {/* Platforms Tab */}
          <div 
            className={`tab-pane fade ${activeTab === 'platforms' ? 'show active' : ''}`}
            id="platforms-tab-pane"
            role="tabpanel"
            aria-labelledby="platforms-tab"
            tabIndex={0}
          >
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Platform Performance</h5>
              </div>
              <div className="card-body">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body text-center">
                        <i className="fab fa-google fa-3x text-primary mb-3"></i>
                        <h6>Google</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Visibility</span>
                          <span className="fw-bold">78%</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Position</span>
                          <span className="fw-bold">#1</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body text-center">
                        <i className="fab fa-microsoft fa-3x text-info mb-3"></i>
                        <h6>Bing</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Visibility</span>
                          <span className="fw-bold">65%</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Position</span>
                          <span className="fw-bold">#2</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Competitors Tab */}
          <div 
            className={`tab-pane fade ${activeTab === 'competitors' ? 'show active' : ''}`}
            id="competitors-tab-pane"
            role="tabpanel"
            aria-labelledby="competitors-tab"
            tabIndex={0}
          >
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Competitor Analysis</h5>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column gap-3">
                  {competitors.map((competitor, index) => (
                    <div key={competitor.name} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                      <div className="d-flex align-items-center gap-3">
                        <span className="small text-muted fw-medium">#{index + 1}</span>
                        <span className="fw-medium">{competitor.name}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className={`small ${getTrendColor(competitor.trend)}`}>
                          {getTrendIcon(competitor.trend)} {competitor.change}%
                        </span>
                        <span className="fw-semibold">{competitor.visibility}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Dashboard;
